import Service, { service } from '@ember/service';
import { query } from '@warp-drive/utilities/json-api';
import {
  deriveSlrLoanStandingAction,
  deriveSlrLoanStandingStatus,
} from 'tio-employee/utils/slr-loan-standing-verification';
import {
  uploadSlrLoanStandingDocumentRequest,
  confirmSlrLoanStandingNameMatchRequest,
} from 'tio-data/requests/slr-loan-standing-verification';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';
import type { EmployeePlanAccount } from 'tio-data/data/resources/employee-plan-account.type';
import type {
  SlrGoodStandingReason,
  SlrGoodStandingState,
  SlrLoanStandingAuditState,
  SlrLoanStandingStatus,
} from 'tio-employee/types/slr-loan-standing-verification';

const GOOD_STANDING_STATES = new Set<SlrGoodStandingState>([
  'unknown',
  'eligible',
  'ineligible',
  'undeterminable',
]);

// Order a problem EPA is surfaced when the employee has several and none are
// eligible — most blocking first. (`processing` is client-only and never
// derived here, but listed for completeness.)
const ATTENTION_PRIORITY: SlrLoanStandingStatus[] = [
  'ineligible',
  'cannot_determine',
  'try_again',
  'pending_upload',
  'unknown',
  'processing',
];

export default class SlrLoanStandingVerificationService extends Service {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;

  /**
   * Read path (tio-front-end#8714): the signed-in employee's current
   * good-standing audit state, read from the JSON:API `employee_plan_accounts`
   * resource (auth-scoped to the user) — replacing the bespoke
   * `/people/:id/slr-loan-standing-verification/current` endpoint that never
   * existed.
   *
   * Account scoping (tio-front-end#9428): alerts/messaging relate only to the
   * account receiving employer contributions — EPAs on the employee's other
   * linked accounts are excluded server-side via `filter[account_id]`. The
   * scope id is the plain `accountReceivingContributionsId` person attribute,
   * which is serialized with the session person from `/me` (no accounts
   * include required — unlike the derived `accountReceivingContributions`
   * getter, which needs materialized accounts and goes null for paid-off /
   * deactivated accounts). When no receiving account exists, there is nothing
   * to message about: returns null WITHOUT falling back to other accounts'
   * EPAs.
   *
   * Multi-EPA aggregation (within the receiving account): if ANY in-scope EPA
   * is `eligible` we surface that; otherwise the most blocking attention
   * state. Returns null when there are no in-scope EPAs.
   *
   * The query is also scoped to the employee's current SLR plan via
   * `filter[employee_plan_id]` when available, preventing cross-plan EPA
   * bleed for employees who have EPAs on multiple plans.
   */
  fetchCurrentState = async () => {
    const receivingAccountId = this.sessionContext.person?.accountReceivingContributionsId;
    if (!receivingAccountId) return null;

    const params: Record<string, string> = { 'filter[account_id]': receivingAccountId };
    const planId = this.sessionContext.currentEmployee?.plan?.id;
    if (planId) {
      params['filter[employee_plan_id]'] = planId;
    }

    const { content } = (await this.store.request(
      query<EmployeePlanAccount>('employee-plan-account', params)
    )) as { content: { data: EmployeePlanAccount[] } };

    return aggregateAuditState(content.data.map(toAuditState));
  };

  // Upload path — blocked on a backend prerequisite (ObservableDocument needs an
  // EPA reference); see tio-front-end#8714. Left calling the not-yet-built
  // endpoints until that lands.
  createVerificationDocument = async (
    employeePlanAccountId: string,
    asset: DigitalAssetDetailSignature
  ) => {
    const { content } = (await this.store.request(
      uploadSlrLoanStandingDocumentRequest(employeePlanAccountId, asset)
    )) as { content: SlrLoanStandingAuditState };
    return content;
  };

  confirmNameMatch = async (employeePlanAccountId: string, confirmed: boolean) => {
    const { content } = (await this.store.request(
      confirmSlrLoanStandingNameMatchRequest(employeePlanAccountId, confirmed)
    )) as { content: SlrLoanStandingAuditState };
    return content;
  };
}

function normalizeGoodStanding(value: string): SlrGoodStandingState {
  return GOOD_STANDING_STATES.has(value as SlrGoodStandingState)
    ? (value as SlrGoodStandingState)
    : 'unknown';
}

function toAuditState(epa: EmployeePlanAccount): SlrLoanStandingAuditState {
  const goodStanding = normalizeGoodStanding(epa.goodStanding);
  const reason = (epa.goodStandingReason || null) as SlrGoodStandingReason | null;

  return {
    employeePlanAccountId: epa.id,
    status: deriveSlrLoanStandingStatus(goodStanding, reason),
    primaryReason: epa.goodStandingReason || null,
    action: deriveSlrLoanStandingAction(goodStanding, reason),
    auditWindow: { openAt: epa.auditWindowOpenAt, closeAt: epa.auditWindowCloseAt },
    contributionPeriod: { start: epa.auditPeriodStart, end: epa.auditPeriodEnd },
    featureEnabled: true,
  };
}

function aggregateAuditState(
  states: SlrLoanStandingAuditState[]
): SlrLoanStandingAuditState | null {
  if (states.length === 0) return null;

  const eligible = states.find((state) => state.status === 'eligible');
  if (eligible) return eligible;

  for (const status of ATTENTION_PRIORITY) {
    const match = states.find((state) => state.status === status);
    if (match) return match;
  }

  return states[0] ?? null;
}
